import { ClickAnalyticsConfig, AnalyticsEvent, TrackingData } from './types';

class ClickAnalyticsCore {
  private config: ClickAnalyticsConfig;
  private sessionId: string;
  private userId?: string;
  private queue: AnalyticsEvent[] = [];
  private isEnabled: boolean = true;

  constructor(config: ClickAnalyticsConfig) {
    this.config = config;
    this.sessionId = this.generateSessionId();
    
    // Check if disabled in development
    if (config.disableInDev && this.isDevelopment()) {
      this.isEnabled = false;
      if (config.debug) {
        console.log('[ClickAnalytics] Disabled in development mode');
      }
      return;
    }

    this.initialize();
  }

  private isDevelopment(): boolean {
    if (typeof process !== 'undefined' && process.env) {
      return process.env.NODE_ENV === 'development';
    }
    if (typeof location !== 'undefined') {
      return location.hostname === 'localhost' || location.hostname === '127.0.0.1';
    }
    return false;
  }

  private initialize() {
    if (this.config.debug) {
      console.log('[ClickAnalytics] Initialized with tracking ID:', this.config.trackingId);
    }
    
    // Auto-track initial page view
    this.pageview();
  }

  private generateSessionId(): string {
    return 'session_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
  }

  private getApiUrl(): string {
    // Use the AWS Lambda function URL by default
    return this.config.apiUrl || 'https://xegjekvvvzkhk6ssyzuih4rdbu0hqbzh.lambda-url.ap-south-1.on.aws/v1/tracker';
  }

  public pageview(url?: string, title?: string): void {
    if (!this.isEnabled) return;

    const event: AnalyticsEvent = {
      type: 'pageview',
      url: url || (typeof window !== 'undefined' ? window.location.pathname : undefined),
      title: title || (typeof document !== 'undefined' ? document.title : undefined),
      timestamp: Date.now(),
      sessionId: this.sessionId,
      userId: this.userId
    };

    this.track(event);
  }

  public event(name: string, properties?: Record<string, any>): void {
    if (!this.isEnabled) return;

    const event: AnalyticsEvent = {
      type: 'event',
      event: name,
      properties: properties || {},
      timestamp: Date.now(),
      sessionId: this.sessionId,
      userId: this.userId,
      url: typeof window !== 'undefined' ? window.location.pathname : undefined
    };

    this.track(event);
  }

  public identify(userId: string): void {
    if (!this.isEnabled) return;
    
    this.userId = userId;
    
    if (this.config.debug) {
      console.log('[ClickAnalytics] User identified:', userId);
    }
  }

  private track(event: AnalyticsEvent): void {
    // Apply beforeSend filter if configured
    if (this.config.beforeSend) {
      const processedEvent = this.config.beforeSend(event);
      if (processedEvent === false) {
        if (this.config.debug) {
          console.log('[ClickAnalytics] Event blocked by beforeSend filter');
        }
        return;
      }
      event = processedEvent;
    }

    this.queue.push(event);
    
    if (this.config.debug) {
      console.log('[ClickAnalytics] Event queued:', event);
    }

    // Send immediately for better reliability (similar to Vercel Analytics)
    this.flush();
  }

  public async flush(): Promise<void> {
    if (!this.isEnabled || this.queue.length === 0) return;

    const events = [...this.queue];
    this.queue = [];

    // Transform events to match Lambda expected format
    const transformedEvents = events.map(event => ({
      trackingId: this.config.trackingId,
      event: event.type === 'pageview' ? 'pageview' : event.event,
      domain: typeof window !== 'undefined' ? window.location.hostname : undefined,
      url: event.url || (typeof window !== 'undefined' ? window.location.href : undefined),
      title: event.title || (typeof document !== 'undefined' ? document.title : undefined),
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,
      sessionId: event.sessionId,
      userId: event.userId,
      timestamp: event.timestamp,
      data: event.type === 'event' ? event.properties : {}
    }));

    // Send in batch format that Lambda expects
    const payload = {
      trackingId: this.config.trackingId,
      events: transformedEvents,
      domain: typeof window !== 'undefined' ? window.location.hostname : undefined
    };

    try {
      const response = await fetch(this.getApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Analytics API error: ${response.status}`);
      }

      if (this.config.debug) {
        console.log('[ClickAnalytics] Events sent successfully:', events.length);
      }
    } catch (error) {
      if (this.config.debug) {
        console.error('[ClickAnalytics] Failed to send events:', error);
      }
      
      // Re-queue events on failure (with limit to prevent memory leaks)
      if (this.queue.length < 100) {
        this.queue.unshift(...events);
      }
    }
  }

  public reset(): void {
    this.sessionId = this.generateSessionId();
    this.userId = undefined;
    this.queue = [];
    
    if (this.config.debug) {
      console.log('[ClickAnalytics] Session reset');
    }
  }

  public setConfig(newConfig: Partial<ClickAnalyticsConfig>): void {
    this.config = { ...this.config, ...newConfig };
    
    if (this.config.debug) {
      console.log('[ClickAnalytics] Config updated');
    }
  }
}

export default ClickAnalyticsCore;